/**
 * useEditableField Hook
 *
 * Reusable hook for managing editable field state and handlers.
 * Handles editing mode, input synchronization, validation, and save operations.
 */

import { useState, useEffect, useCallback, useRef } from "react";

export interface UseEditableFieldOptions {
  value: string;
  onSave?: (newValue: string) => void;
  onEdit?: () => void;
  editable?: boolean;
  isSaving?: boolean;
  isError?: boolean;
  onBlur?: (value: string) => boolean | void;
  /**
   * Custom handler for when editing starts
   * If provided, will be called instead of default setIsEditing(true)
   */
  onStartEdit?: () => void;
}

export interface UseEditableFieldResult {
  isEditing: boolean;
  inputValue: string;
  inputRef: React.RefObject<HTMLInputElement>;
  canEdit: boolean;
  handleStartEdit: () => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleBlur: () => void;
}

/**
 * Hook to manage editable field state and handlers
 *
 * Provides:
 * - Editing state management
 * - Input value synchronization
 * - Save/cancel handlers
 * - Keyboard navigation (Enter/Escape)
 * - Blur handling with validation
 * - Focus management
 *
 * @example
 * ```tsx
 * const {
 *   isEditing,
 *   inputValue,
 *   inputRef,
 *   canEdit,
 *   handleStartEdit,
 *   handleInputChange,
 *   handleKeyDown,
 *   handleBlur,
 * } = useEditableField({
 *   value: "John",
 *   onSave: (newValue) => console.log(newValue),
 *   onBlur: (value) => {
 *     // Validate and return true if error
 *     return value.length < 3;
 *   },
 * });
 * ```
 */
export function useEditableField({
  value,
  onSave,
  onEdit,
  editable = true,
  isSaving = false,
  isError = false,
  onBlur: onBlurCallback,
  onStartEdit,
}: UseEditableFieldOptions): UseEditableFieldResult {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value ?? "");
  const inputRef = useRef<HTMLInputElement>(null);
  const originalValueRef = useRef<string>(value ?? "");

  // Sync inputValue with external value when not editing
  useEffect(() => {
    if (!isEditing) {
      setInputValue(value ?? "");
      originalValueRef.current = value ?? "";
    }
  }, [value, isEditing]);

  // Handle focus on edit and force close when saving starts.
  useEffect(() => {
    if (!isEditing) return;

    if (isSaving) {
      setIsEditing(false);
      return;
    }

    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isSaving, isEditing]);

  const canEdit = editable && !isSaving;

  const handleStartEdit = useCallback(() => {
    if (!canEdit) return;

    if (onStartEdit) {
      onStartEdit();
    } else if (onEdit) {
      onEdit();
    } else {
      setInputValue(value);
      originalValueRef.current = value;
      setIsEditing(true);
    }
  }, [canEdit, onStartEdit, onEdit, value]);

  const handleSave = useCallback(
    (newValue: string) => {
      if (!isEditing || isSaving) return;
      if (isError) return;

      const trimmedNewValue = newValue.trim();
      const trimmedOriginal = originalValueRef.current.trim();

      if (trimmedNewValue === trimmedOriginal) {
        setIsEditing(false);
        return;
      }

      setIsEditing(false);
      if (onSave) onSave(trimmedNewValue);
    },
    [isEditing, isSaving, isError, onSave]
  );

  const handleCancel = useCallback(() => {
    setInputValue(originalValueRef.current);
    setIsEditing(false);
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isSaving) return;
      setInputValue(e.target.value);
    },
    [isSaving]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (isSaving) return;

      if (e.key === "Enter") {
        e.preventDefault();
        inputRef.current?.blur();
      } else if (e.key === "Escape") {
        e.preventDefault();
        handleCancel();
      }
    },
    [isSaving, handleCancel]
  );

  const handleBlur = useCallback(() => {
    if (isSaving) return;

    let hasError = false;
    if (onBlurCallback) {
      const result = onBlurCallback(inputValue);
      hasError = result === true;
    }

    if (hasError) return;

    setIsEditing(false);

    if (!onBlurCallback) {
      handleSave(inputValue);
    }
  }, [isSaving, inputValue, onBlurCallback, handleSave]);

  return {
    isEditing,
    inputValue,
    inputRef: inputRef as React.RefObject<HTMLInputElement>,
    canEdit,
    handleStartEdit,
    handleInputChange,
    handleKeyDown,
    handleBlur,
  };
}

